import React, { useState, useEffect, useMemo } from "react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { ui } from "../config/theme";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const JobPostingForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        location: "",
        salary: "",
        status: "Active",
        closing_date: "",
        responsibilities: "",
        department: "",
        employment_type: "",
        classification: "",
        skills: "",
        is_remote: false,
        include_salary: false,
        currency: "USD",
        paid_every: "Month",
        minimum_salary: "",
        maximum_salary: "",
        location_country: "Sri Lanka",
        province: "",
        district: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countries, setCountries] = useState([]);
    const [initializedCountry, setInitializedCountry] = useState(false);
    const [lkLocations, setLkLocations] = useState([]);
    const isSriLanka = useMemo(() => formData.location_country === 'Sri Lanka', [formData.location_country]);
    const provinceOptions = useMemo(() => (lkLocations || []).map(p => p.province), [lkLocations]);
    const districtOptions = useMemo(() => {
        const p = (lkLocations || []).find(x => x.province === formData.province);
        return p ? (p.districts || []).map(d => d.district) : [];
    }, [lkLocations, formData.province]);

    const { user } = useAuth();

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ 
            ...formData, 
            [name]: type === 'checkbox' ? checked : value 
        });
    };

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch('https://general.apivcm.shop/countries', { cache: 'no-store' });
                if (r.ok) {
                    const j = await r.json();
                    const arr = Array.isArray(j?.data) ? j.data : [];
                    const normalized = arr.map(c => ({
                        id: c.id,
                        label: c.country,
                        value: c.country_code === 'LK' ? 'Sri Lanka' : c.country,
                        code: c.country_code
                    }));
                    // Ensure 'Sri Lanka' exists
                    const hasLK = normalized.some(c => c.value === 'Sri Lanka');
                    const finalList = hasLK ? normalized : [{ id: 'LK', label: 'Sri Lanka', value: 'Sri Lanka', code: 'LK' }, ...normalized];
                    setCountries(finalList);
                    // If current selection not in list, default to Sri Lanka
                    const exists = finalList.some(c => (c.value || '').trim().toLowerCase() === ((formData.location_country || 'Sri Lanka').trim().toLowerCase()))
                    if (!exists) setFormData(prev => ({ ...prev, location_country: 'Sri Lanka' }));
                    // On first load, force default to Sri Lanka to avoid UI showing first item
                    if (!initializedCountry) {
                        setFormData(prev => ({ ...prev, location_country: 'Sri Lanka' }));
                        setInitializedCountry(true);
                    }
                }
            } catch {}
        })();
        (async () => {
            try {
                const r = await fetch('https://general.apivcm.shop/sri-lanka-locations', { cache: 'no-store' });
                if (r.ok) {
                    const j = await r.json();
                    setLkLocations(Array.isArray(j?.data) ? j.data : []);
                }
            } catch {}
        })();
    }, []);

    useEffect(() => {
        if (!isSriLanka) {
            setFormData(prev => ({ ...prev, province: "", district: "" }));
        }
    }, [isSriLanka]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const composedLocation = formData.location?.trim()?.length ? formData.location : (
                formData.location_country === 'Sri Lanka'
                    ? [formData.district, formData.province, 'Sri Lanka'].filter(Boolean).join(', ')
                    : formData.location_country
            );

            const jobData = {
                ...formData,
                location: composedLocation,
                posted_by: user?.id,
                // Convert salary fields to numbers if they exist
                minimum_salary: formData.minimum_salary ? parseFloat(formData.minimum_salary) : null,
                maximum_salary: formData.maximum_salary ? parseFloat(formData.maximum_salary) : null,
                salary: formData.salary ? parseFloat(formData.salary) : null
            };

            const response = await fetch(`${API_BASE_URL}/api/job-postings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(jobData),
            });

            if (response.ok) {
                navigate("/recruiter-dashboard");
            } else {
                throw new Error("Failed to post job");
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    <div className="w-full mb-6">
                        <button
                            onClick={() => navigate(-1)}
                            className={ui.buttonLink}
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back
                        </button>
                    </div>
                    
                    <div className={`${ui.sectionCard} p-8`}>
                        <div className="flex items-center justify-between mb-8">
                            <h1 className="text-3xl font-semibold text-gray-900">Create New Job</h1>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">Active</span>
                                <div className="w-12 h-6 bg-green-500 rounded-full flex items-center">
                                    <div className="w-5 h-5 bg-white rounded-full ml-1"></div>
                                </div>
                            </div>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Job Information Section */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Information</h2>
                                
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                                            Job Title <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="title"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="e.g. Software Engineer"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                                                Department <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                id="department"
                                                name="department"
                                                value={formData.department}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                <option value="Engineering">Engineering</option>
                                                <option value="Marketing">Marketing</option>
                                                <option value="Sales">Sales</option>
                                                <option value="HR">Human Resources</option>
                                                <option value="Finance">Finance</option>
                                                <option value="Operations">Operations</option>
                                                <option value="Design">Design</option>
                                                <option value="Product">Product</option>
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="employment_type" className="block text-sm font-medium text-gray-700">
                                                Employment Type <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                id="employment_type"
                                                name="employment_type"
                                                value={formData.employment_type}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                <option value="Full-time">Full-time</option>
                                                <option value="Part-time">Part-time</option>
                                                <option value="Contract">Contract</option>
                                                <option value="Temporary">Temporary</option>
                                                <option value="Internship">Internship</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="classification" className="block text-sm font-medium text-gray-700">
                                            Classification <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="classification"
                                            name="classification"
                                            value={formData.classification}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            <option value="">Select...</option>
                                            <option value="Entry Level">Entry Level</option>
                                            <option value="Mid Level">Mid Level</option>
                                            <option value="Senior Level">Senior Level</option>
                                            <option value="Executive">Executive</option>
                                            <option value="Manager">Manager</option>
                                            <option value="Director">Director</option>
                                        </select>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="skills" className="block text-sm font-medium text-gray-700">
                                            Skills
                                        </label>
                                        <input
                                            type="text"
                                            id="skills"
                                            name="skills"
                                            value={formData.skills}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="Enter skills separated by commas"
                                        />
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="is_remote"
                                            name="is_remote"
                                            checked={formData.is_remote}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="is_remote" className="ml-2 text-sm text-gray-700">
                                            This is a work from home/remote position
                                        </label>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">Country <span className="text-red-500">*</span></label>
                                        <select
                                            name="location_country"
                                            value={formData.location_country}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            {countries.map(c => (
                                                <option key={c.id} value={c.value}>{c.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {isSriLanka && (
                                        <>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">Province <span className="text-red-500">*</span></label>
                                            <select
                                                name="province"
                                                value={formData.province}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                {provinceOptions.map(p => (<option key={p} value={p}>{p}</option>))}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium text-gray-700">District <span className="text-red-500">*</span></label>
                                            <select
                                                name="district"
                                                value={formData.district}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            >
                                                <option value="">Select...</option>
                                                {districtOptions.map(d => (<option key={d} value={d}>{d}</option>))}
                                            </select>
                                        </div>
                                        </>
                                    )}
                                    {!isSriLanka && (
                                        <div className="space-y-2">
                                            <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
                                            <input
                                                type="text"
                                                id="location"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                placeholder="City or region"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Job Description Section */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Description</h2>
                                
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                            Job Description <span className="text-red-500">*</span>
                                        </label>
                                        <div className="border border-gray-300 rounded-lg">
                                            <div className="border-b border-gray-300 px-3 py-2 bg-gray-50 rounded-t-lg">
                                                <div className="flex items-center space-x-2 text-sm">
                                                    <button type="button" className="p-1 hover:bg-gray-200 rounded">B</button>
                                                    <button type="button" className="p-1 hover:bg-gray-200 rounded italic">I</button>
                                                    <button type="button" className="p-1 hover:bg-gray-200 rounded underline">U</button>
                                                    <select className="text-sm border-0 bg-transparent">
                                                        <option>Normal</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <textarea
                                                id="description"
                                                name="description"
                                                rows="6"
                                                value={formData.description}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border-0 focus:ring-0 resize-none rounded-b-lg"
                                                placeholder="You need at least 50 more words"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="responsibilities" className="block text-sm font-medium text-gray-700">
                                            Key Responsibilities <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="responsibilities"
                                            name="responsibilities"
                                            rows="4"
                                            value={formData.responsibilities}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                                            placeholder="List the main responsibilities and duties..."
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Salary Information Section */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Salary Information</h2>
                                
                                <div className="space-y-6">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="include_salary"
                                            name="include_salary"
                                            checked={formData.include_salary}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor="include_salary" className="ml-2 text-sm text-gray-700">
                                            Include Salary Information
                                        </label>
                                    </div>

                                    {formData.include_salary && (
                                        <div className="space-y-6 p-4 bg-gray-50 rounded-lg">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                                                        Currency <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        id="currency"
                                                        name="currency"
                                                        value={formData.currency}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                    >
                                                        <option value="USD">USD</option>
                                                        <option value="EUR">EUR</option>
                                                        <option value="GBP">GBP</option>
                                                        <option value="CAD">CAD</option>
                                                        <option value="LKR">LKR</option>
                                                    </select>
                                                </div>

                                                <div className="space-y-2">
                                                    <label htmlFor="paid_every" className="block text-sm font-medium text-gray-700">
                                                        Paid Every <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        id="paid_every"
                                                        name="paid_every"
                                                        value={formData.paid_every}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                    >
                                                        <option value="Month">Month</option>
                                                        <option value="Year">Year</option>
                                                        <option value="Hour">Hour</option>
                                                        <option value="Week">Week</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="space-y-2">
                                                    <label htmlFor="minimum_salary" className="block text-sm font-medium text-gray-700">
                                                        Minimum Salary <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="minimum_salary"
                                                        name="minimum_salary"
                                                        value={formData.minimum_salary}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                        placeholder="0"
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <label htmlFor="maximum_salary" className="block text-sm font-medium text-gray-700">
                                                        Maximum Salary <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        id="maximum_salary"
                                                        name="maximum_salary"
                                                        value={formData.maximum_salary}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Application Details */}
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Details</h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label htmlFor="closing_date" className="block text-sm font-medium text-gray-700">
                                            Application Closing Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            id="closing_date"
                                            name="closing_date"
                                            value={formData.closing_date}
                                            onChange={handleChange}
                                            required
                                            min={new Date().toISOString().split('T')[0]}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                                            Status
                                        </label>
                                        <select
                                            id="status"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Closed">Closed</option>
                                            <option value="Draft">Draft</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Buttons */}
                            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => navigate(-1)}
                                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-all duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-8 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-500 focus:ring-opacity-50 transition-all duration-200 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            + Create new job
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default JobPostingForm;